import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { allArticles, categories } from '@/mocks/site-data';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();

  const article = useMemo(
    () => allArticles.find((a) => a.id === articleId),
    [articleId]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const category = useMemo(
    () => categories.find((c) => c.id === article?.categoryId),
    [article]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [articleId]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18">
          <section className="py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaOrg
        type="article"
        articleTitle={article.title}
        articleDescription={article.excerpt}
        articleDate={article.publishDate}
        articleCategory={article.categoryId}
        categoryName={article.categoryName}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              {category && (
                <>
                  <Link
                    to={`/category/${category.slug}`}
                    className="hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </>
              )}
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 text-xs font-medium text-accent-700 bg-accent-100 rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-snug mb-4">
                {article.title}
              </h1>

              <p className="text-sm text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div
              className="article-content prose prose-sm max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3 flex-1">
                      {ra.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{ra.readTime}</span>
                      <span>{ra.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}